/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { collection } from "firebase/firestore"
import { db } from "../../firebase"
import { Box, Button, FormControl, InputLabel, MenuItem, Paper, Select, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material"
import PrintIcon from '@mui/icons-material/Print'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import dayjs from "dayjs"
import useCollectionSubscription from "../../components/hooks/useCollectionSubscription"
import { Customer } from "../../models/customer"

type QuotationProduct = {
  id: string
  productNo?: string
  individualNo?: string
  part?: string
  weight?: number | string
  storageType?: string
  quantity?: number
  price?: number
  taxRate?: number
}

type QuotationData = {
  products: QuotationProduct[]
  totalCount?: number
  totalWeight?: number
  totalPrice?: number
  customer?: string
  shippingFee?: number
  memo?: string
}

const ISSUER = {
  name: 'りんどう',
  address: '北海道歌志内市字歌神36',
  phone: '0125-35-9107',
}

// 税率が未設定の商品（部位マスタ登録前の旧データ等）は8%（軽減税率）とみなす
const DEFAULT_TAX_RATE = 8
const STORAGE_KEY = 'quotationPreviewData'

const QuotationPreview = () => {
  const navigate = useNavigate()
  const [data, setData] = useState<QuotationData | null>(null)
  const [customer, setCustomer] = useState<string>('')
  const [recipient, setRecipient] = useState<string>('')
  const [honorific, setHonorific] = useState<'御中' | '様'>('御中')
  const [shippingFee, setShippingFee] = useState<number>(0)
  const [memo, setMemo] = useState<string>('')
  const customers = useCollectionSubscription(collection(db, 'customers'), Customer)

  useEffect(() => {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return
    try {
      const parsed: QuotationData = JSON.parse(raw)
      setData(parsed)
      if (parsed.customer) { setCustomer(parsed.customer); setRecipient(parsed.customer) }
      if (typeof parsed.shippingFee === 'number') setShippingFee(parsed.shippingFee)
      if (typeof parsed.memo === 'string') setMemo(parsed.memo)
    } catch (e) {
      console.error('見積データの読み込みに失敗:', e)
    }
  }, [])

  // 入力内容を sessionStorage に保存（リロード対策）
  useEffect(() => {
    if (!data) return
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ ...data, customer, shippingFee, memo }))
  }, [customer, shippingFee, memo, data])

  // 印刷（PDF保存）ダイアログの初期ファイル名として利用される
  useEffect(() => {
    if (!data) return
    const originalTitle = document.title
    const datePart = dayjs().format('YYYYMMDD')
    document.title = `見積書${recipient}_${datePart}.pdf`
    return () => { document.title = originalTitle }
  }, [data, recipient])

  const products = useMemo(() => data?.products ?? [], [data])
  const productTotalIncTax = useMemo(() => {
    if (typeof data?.totalPrice === 'number') return data.totalPrice
    return products.reduce((sum, p) => sum + (p.price || 0), 0)
  }, [data, products])

  // 税率ごと（8%・10%混在時はインボイス制度上、税率ごとに区分して記載する必要がある）に小計・消費税を集計
  const taxGroups = useMemo(() => {
    const amountByRate = new Map<number, number>()
    products.forEach((p) => {
      const rate = p.taxRate === 10 ? 10 : DEFAULT_TAX_RATE
      amountByRate.set(rate, (amountByRate.get(rate) || 0) + (p.price || 0))
    })
    if (amountByRate.size === 0 && productTotalIncTax > 0) {
      amountByRate.set(DEFAULT_TAX_RATE, productTotalIncTax)
    }
    return Array.from(amountByRate.entries())
      .sort(([a], [b]) => a - b)
      .map(([rate, incTax]) => {
        const subtotal = Math.round(incTax / (1 + rate / 100))
        return { rate, incTax, subtotal, tax: incTax - subtotal }
      })
  }, [products, productTotalIncTax])

  const grandTotal = productTotalIncTax + (shippingFee || 0)

  const issuedDate = dayjs().format('YYYY年MM月DD日')

  if (!data) {
    return (
      <Box m={4}>
        <Typography variant="h5">見積データが見つかりません</Typography>
        <Typography variant="body2" color="text.secondary" mt={1}>
          商品番号検索の一覧から「見積書を作成」ボタンを押して開いてください。
        </Typography>
        <Box mt={2}>
          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/search/product')}>
            商品番号検索に戻る
          </Button>
        </Box>
      </Box>
    )
  }

  return (
    <Box>
      {/* 印刷時にブラウザ既定のヘッダ/フッタ（URL・日付・ページ番号）を非表示にする */}
      <style dangerouslySetInnerHTML={{ __html: `
        @page { size: A4; margin: 15mm; }
        @media print {
          html, body { margin: 0 !important; }
          table { page-break-inside: auto; }
          tr { page-break-inside: avoid; break-inside: avoid-page; }
          thead { display: table-header-group; }
        }
      ` }} />
      {/* 操作バー（印刷時は非表示） */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          gap: 2,
          alignItems: 'center',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          '@media print': { display: 'none' },
        }}
      >
        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>
          戻る
        </Button>
        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="quotation-customer-label">取引先</InputLabel>
            <Select
              labelId="quotation-customer-label"
              label="取引先"
              value={customer}
              onChange={(e) => { setCustomer(e.target.value); setRecipient(e.target.value) }}
            >
              <MenuItem value=""><em>未指定</em></MenuItem>
              <MenuItem value="自社">自社</MenuItem>
              {customers.map((c: Customer) => (
                <MenuItem key={c.id ?? c.companyName} value={c.companyName}>{c.companyName}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="宛先"
            size="small"
            value={recipient}
            onChange={(e) => setRecipient(e.target.value)}
            sx={{ width: 200 }}
          />
          <ToggleButtonGroup
            value={honorific}
            exclusive
            size="small"
            onChange={(_, v) => { if (v) setHonorific(v) }}
          >
            <ToggleButton value="御中">御中</ToggleButton>
            <ToggleButton value="様">様</ToggleButton>
          </ToggleButtonGroup>
          <TextField
            label="送料（税込）"
            type="number"
            size="small"
            value={shippingFee}
            onChange={(e) => setShippingFee(Math.max(0, parseInt(e.target.value) || 0))}
            inputProps={{ min: 0, step: 100 }}
            sx={{ width: 160 }}
          />
          <TextField
            label="備考"
            size="small"
            multiline
            minRows={1}
            maxRows={4}
            value={memo}
            onChange={(e) => setMemo(e.target.value)}
            sx={{ width: 280 }}
          />
          <Button variant="contained" startIcon={<PrintIcon />} onClick={() => window.print()}>
            印刷 / PDF保存
          </Button>
        </Box>
      </Box>

      {/* 帳票本体 */}
      <Box
        sx={{
          maxWidth: '210mm',
          minHeight: '297mm',
          mx: 'auto',
          p: '20mm',
          bgcolor: 'white',
          color: '#000',
          fontFamily: '"Hiragino Sans", "Yu Gothic", "Meiryo", sans-serif',
          '@media print': {
            maxWidth: 'none',
            minHeight: 'auto',
            margin: 0,
            padding: 0,
            boxShadow: 'none',
          },
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
          <Box>
            <Typography variant="body2">発行日: {issuedDate}</Typography>
          </Box>
          <Typography variant="h4" sx={{ borderBottom: '3px double #000', px: 4, py: 1, fontWeight: 'bold' }}>
            御見積書
          </Typography>
          <Box sx={{ width: 120 }} />
        </Box>

        <Box display="flex" justifyContent="space-between" alignItems="flex-end" mb={4}>
          <Box>
            <Typography variant="h5" sx={{ borderBottom: '1px solid #000', pb: 0.5, mb: 1, minWidth: '60mm' }}>
              {recipient || ''} {honorific}
            </Typography>
            <Typography variant="body2">下記の通り、御見積申し上げます。</Typography>
          </Box>
          <Box textAlign="right">
            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{ISSUER.name}</Typography>
            <Typography variant="body2">{ISSUER.address}</Typography>
            <Typography variant="body2">TEL: {ISSUER.phone}</Typography>
          </Box>
        </Box>

        <Box mb={3} display="flex" justifyContent="flex-end">
          <Box sx={{ border: '2px solid #000', px: 3, py: 1.5 }}>
            <Typography variant="body2" textAlign="center">御見積金額（税込）</Typography>
            <Typography variant="h4" textAlign="right" sx={{ fontWeight: 'bold' }}>
              ¥{grandTotal.toLocaleString('ja-JP')}
            </Typography>
          </Box>
        </Box>

        <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
          <Table size="small" sx={{ '& td, & th': { color: '#000', borderColor: '#000' } }}>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f0f0f0' }}>
                <TableCell sx={{ fontWeight: 'bold', width: '40px' }}>No</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>商品番号</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>品名（部位）</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>税率</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>温度</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>重量(g)</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>数量</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>金額（税込）</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {products.map((p, i) => (
                <TableRow key={p.id || i}>
                  <TableCell>{i + 1}</TableCell>
                  <TableCell>{p.productNo || '-'}</TableCell>
                  <TableCell>{p.part || '-'}</TableCell>
                  <TableCell>{p.taxRate === 10 ? 10 : DEFAULT_TAX_RATE}%</TableCell>
                  <TableCell>{p.storageType || '-'}</TableCell>
                  <TableCell align="right">{p.weight ?? '-'}</TableCell>
                  <TableCell align="right">{p.quantity ?? 1}</TableCell>
                  <TableCell align="right">
                    {p.price != null ? `¥${Number(p.price).toLocaleString('ja-JP')}` : '-'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* 集計（税率ごとに区分して記載） */}
        <Box display="flex" justifyContent="flex-end" mb={3} sx={{ '@media print': { pageBreakInside: 'avoid', breakInside: 'avoid' } }}>
          <Box sx={{ minWidth: '80mm', border: '1px solid #000' }}>
            {taxGroups.map((g) => (
              <Box key={g.rate} display="flex" justifyContent="space-between" px={2} py={0.5} sx={{ borderBottom: '1px solid #000' }}>
                <Typography variant="body2">
                  商品小計（税抜・{g.rate}%対象）／消費税（{g.rate}%）
                </Typography>
                <Typography variant="body2">
                  ¥{g.subtotal.toLocaleString('ja-JP')} ／ ¥{g.tax.toLocaleString('ja-JP')}
                </Typography>
              </Box>
            ))}
            <Box display="flex" justifyContent="space-between" px={2} py={0.5} sx={{ borderBottom: '1px solid #000' }}>
              <Typography variant="body2">商品計（税込）</Typography>
              <Typography variant="body2">¥{productTotalIncTax.toLocaleString('ja-JP')}</Typography>
            </Box>
            <Box display="flex" justifyContent="space-between" px={2} py={0.5} sx={{ borderBottom: '1px solid #000' }}>
              <Typography variant="body2">送料（税込）</Typography>
              <Typography variant="body2">¥{(shippingFee || 0).toLocaleString('ja-JP')}</Typography>
            </Box>
            <Box display="flex" justifyContent="space-between" px={2} py={0.5} sx={{ bgcolor: '#f0f0f0' }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>合計（税込）</Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>¥{grandTotal.toLocaleString('ja-JP')}</Typography>
            </Box>
          </Box>
        </Box>

        {memo && (
          <Box mb={3} sx={{ '@media print': { pageBreakInside: 'avoid', breakInside: 'avoid' } }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>備考</Typography>
            <Typography variant="body2" sx={{ borderTop: '1px solid #000', pt: 1, whiteSpace: 'pre-wrap' }}>
              {memo}
            </Typography>
          </Box>
        )}

        <Box mt={6}>
          <Typography variant="body2" color="text.secondary">
            ※ 本見積書の有効期限は発行日より7日間です。
          </Typography>
        </Box>
      </Box>
    </Box>
  )
}

export default QuotationPreview
